import React from "react";

import styled from "styled-components";

import type { IconName } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { ListItemIndustry, TabPanel, TabList, Tab, Tabs, Textfield } from "@similarweb/ui-components";

import categoryService from "app/common/services/categoryService";
import type { ICategory } from "app/common/services/categoryService.types";
import { InnerTitleContainer } from "pro-features/components/AutocompleteKeywords/styles/AutocompleteKeywordStyles";
import { ScrollAreaWrap } from "pro-features/components/AutocompleteWebCategories/utils";
import { i18nFilter } from "app/filters/i18nFilter";
import { CATEGORIES_TO_DISPLAY } from "app/services/category/userCustomCategoryService";
import { CategoryServiceUtils } from "app/services/CategoryServiceUtils";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";

const CategoriesFilterContainer = styled.div``;

const SearchInput = styled(Textfield)`
    padding: 7px 0px;
    margin: 0px 4px;
    border-bottom: 1px solid ${colorsPalettes.blue[400]};
`;

const TabPanelsContainer = styled.div`
    max-height: 300px;
    overflow-y: auto;
`;

const ListItemIndustryContainer = styled.div`
    cursor: pointer;
`;

const createListItem = (onClickCallback: FixmeType) =>
    function (item: FixmeType) {
        const { getText, text, parentItem, icon, id, count, disabled = false } = item;
        const enforceIcon = false;
        const isChild = !enforceIcon && item.isChild;
        const displayText = isChild ? text : getText ? getText() : text;

        return (
            <ListItemIndustryContainer>
                <ListItemIndustry
                    iconClassName={enforceIcon && item.isChild ? parentItem.icon : icon}
                    key={id}
                    onClick={onClickCallback(item)}
                    text={displayText + ` (${count})`}
                    isChildren={isChild}
                    isParent={!item.isChild}
                    disabled={disabled}
                    tooltipText={disabled ? i18n("digital.marketing.custom.industries.not.supported") : undefined}
                />
            </ListItemIndustryContainer>
        );
    };

const defaultFilter = (query: string) => (item: FixmeType) => item.text.toLowerCase().includes(query.toLocaleString());

interface ICategoryItem {
    text: string;
    icon?: string;
    id?: string;
    sons?: ICategoryItem[];
}

interface ICategoriesFilterProps {
    onItemClickCallback: (item: ICategoryItem) => void;
    categories: ICategory[];
    customCategories?: ICategory[];
    rightTabTitle?: string;
    leftTabTitle?: string;
    searchIcon?: IconName;
    searchPlaceholder?: string;
    categoriesFilterClassName?: string;
    categoriesFilterTabsPanelClassName?: string;
}

export const CategoriesFilter: React.FunctionComponent<ICategoriesFilterProps> = (props) => {
    const {
        searchIcon = "search",
        searchPlaceholder = i18n("dropdown.category.search.placeholder"),
        categoriesFilterClassName = "categories-filter-container",
        categoriesFilterTabsPanelClassName = "categories-filter-tab-panels-container",
        categories: categoriesRaw,
        customCategories: customCategoriesRaw,
        leftTabTitle = i18n("common.auto.complete.industries"),
        rightTabTitle = i18n("common.auto.complete.custom.industries"),
        onItemClickCallback,
    } = props;
    const [selectedTabIndex, setSelectedTabIndex] = React.useState(Number());
    const [searchInputValue, setSearchInputValue] = React.useState(String());
    const onTabSelected = (index: number) => setSelectedTabIndex(index);
    const onClickCallback = (item: FixmeType) => () => onItemClickCallback(item);
    const {
        categories: common,
        customCategories: custom,
        sharedCustomCategories: shared,
    } = categoryService.parseCategoriesFromApiToCategoriesFilter({
        categories: categoriesRaw,
        allCustomCategories: customCategoriesRaw ?? [],
    });
    const categoriesView = common.filter(defaultFilter(searchInputValue));
    const customCategoriesView = custom.filter(defaultFilter(searchInputValue));
    const sharedCustomCategoriesView = shared.filter(defaultFilter(searchInputValue));

    const hasSegmentsInIndustry = (customCategory: FixmeType) => {
        // @ts-ignore FIXME: strict migration - Object is possibly 'undefined' — userCustomCategoriesObj lookup may be undefined; comparison already yields false at runtime
        if (CategoryServiceUtils.userCustomCategoriesObj[customCategory?.id]?.segments?.length > 0) {
            return { ...customCategory, disabled: true };
        }
        return { ...customCategory, disabled: false };
    };

    const createCustomListItem = (customCategory: FixmeType) => {
        const newCustomCategory = hasSegmentsInIndustry(customCategory);
        return createListItem(onClickCallback)(newCustomCategory);
    };

    return (
        <CategoriesFilterContainer className={categoriesFilterClassName}>
            <SearchInput
                inputFocusParameters={{ preventScroll: true }}
                isFocused={true}
                hideBorder={true}
                iconName={searchIcon}
                placeholder={searchPlaceholder}
                onChange={setSearchInputValue}
            />
            <Tabs
                selectedIndex={selectedTabIndex}
                onSelect={onTabSelected}
            >
                <TabList>
                    {customCategoriesRaw ? (
                        <>
                            <Tab>{`${leftTabTitle} (${categoriesView.length})`}</Tab>
                            <Tab>
                                {`${rightTabTitle} (${
                                    customCategoriesView.length + sharedCustomCategoriesView.length
                                })`}
                            </Tab>
                        </>
                    ) : null}
                </TabList>
                <TabPanelsContainer className={categoriesFilterTabsPanelClassName}>
                    <TabPanel>{categoriesView.map(createListItem(onClickCallback))}</TabPanel>
                    <TabPanel>
                        <ScrollAreaWrap>
                            <FlexColumn>
                                <InnerTitleContainer>
                                    {i18nFilter()("autocomplete.custom.categories.tab.my.section", {
                                        amount: custom.length,
                                    })}
                                </InnerTitleContainer>
                                {custom.map((customCategory) => createCustomListItem(customCategory))}
                            </FlexColumn>
                            <FlexColumn>
                                <InnerTitleContainer>
                                    {i18nFilter()("autocomplete.custom.categories.tab.shared.section", {
                                        amount: shared.length,
                                    })}
                                </InnerTitleContainer>
                                {shared.slice(0, CATEGORIES_TO_DISPLAY).map(createListItem(onClickCallback))}
                            </FlexColumn>
                        </ScrollAreaWrap>
                    </TabPanel>
                </TabPanelsContainer>
            </Tabs>
        </CategoriesFilterContainer>
    );
};

const i18n = i18nFilter();
